import type { CSSProperties } from 'react';
import type { SpectrumModel } from '@/lib/pipelineQuery';
import { plural } from '@/lib/pipelineQuery';

interface Props {
  model: SpectrumModel;
  pipelineName: string;
  onSelect: (stageId: string | null) => void;
}

/**
 * Полоса воронки для сделок (прототип: spectrum(…, deals=true)): отрезки одинаковой ширины по этапам,
 * в отрезке — число сделок, над полосой — «N сделок» и сумма, под полосой — названия этапов.
 * Нажатие оставляет один этап; повторное — снимает. Этап без сделок показывает 0 и не нажимается.
 */
export function DealSpectrum({ model, pipelineName, onSelect }: Props) {
  if (model.segments.length === 0) return null;
  const sel = model.selected;
  return (
    <div className="spec">
      <div className="spec-cap">
        {sel ? (
          <>
            <span className="sdot" style={{ background: sel.color }} aria-hidden />
            <b>{sel.name}</b>
            <span className="tnum">{model.count}{model.sumText ? `, ${model.sumText}` : ''}</span>
            <button type="button" className="clr" onClick={() => onSelect(null)}>Все стадии</button>
          </>
        ) : (
          <>
            <b className="tnum">{model.count} {plural(model.count, 'сделка', 'сделки', 'сделок')}</b>
            {model.sumText && <span className="sum tnum">{model.sumText}</span>}
          </>
        )}
      </div>
      <div className={`bar${sel ? ' picked' : ''}`} role="group" aria-label={`Стадии воронки ${pipelineName}`}>
        {model.segments.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`seg tnum${s.count === 0 ? ' zero' : ''}`}
            style={{ '--c': s.color } as CSSProperties}
            aria-pressed={sel?.id === s.id}
            aria-label={`${s.name}: ${s.count}`}
            disabled={s.count === 0 && sel?.id !== s.id}
            onClick={() => onSelect(sel?.id === s.id ? null : s.id)}
          >
            {s.count}
          </button>
        ))}
      </div>
      <div className="spec-names" aria-hidden>
        {model.segments.map((s) => <span key={s.id} className={sel?.id === s.id ? 'on' : ''}>{s.name}</span>)}
      </div>
    </div>
  );
}
